import React, { useMemo, useState } from 'react';
import { Badge, Row } from 'reactstrap';
import { HistoryItem } from '../types/template-history.types';
import TableContainer from "../../../Components/Common/TableContainer";
import AlertModal from "../../../Components/Common/AlertModal";
// Asegúrate de ajustar la ruta de importación de tu TableContainer global

type Props = {
    data: HistoryItem[];
    total: number;
    filters: { page: number; limit: number };
    isLoading: boolean;
    isFetching?: boolean;
    onPageChange: (page: number) => void;
};

const renderErrorMessage = (error: any): React.ReactNode => {
    if (!error) return <span className="text-muted">No hay mensaje de error disponible</span>;
    if (typeof error === 'string') return <span className="text-muted">{error}</span>;
    if (typeof error === 'object') {
        return (
            <div className="text-start">
                {error.message && (
                    <div className="mb-2">
                        <strong className="text-dark">Mensaje:</strong>
                        <p className="text-muted mb-0">{error.message}</p>
                    </div>
                )}
                {Array.isArray(error.details) && error.details.length > 0 && (
                    <div className="mb-2">
                        <strong className="text-dark">Detalles:</strong>
                        <ul className="text-muted ps-3 mb-0" style={{ listStyleType: 'disc' }}>
                            {error.details.map((detail: string, index: number) => (
                                <li key={index}>{detail}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {error.code && (
                    <div className="mt-2 text-end">
                        <span className="badge bg-danger-subtle text-danger" style={{ fontSize: '10px' }}>
                            Código: {error.code}
                        </span>
                    </div>
                )}
            </div>
        );
    }
    return <span className="text-muted">{String(error)}</span>;
};

export const TemplateHistoryTable = ({
    data,
    total,
    filters,
    isLoading,
    isFetching = false,
    onPageChange
}: Props) => {

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<any>('');

    // 1. Configuración de columnas requerida por TanStack Table
    const columns = useMemo(
        () => [
            {
                header: "Canal",
                accessorKey: "templateType",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    const type = cellProps.getValue();
                    return type === 'WHATSAPP' ? (
                        <Badge color="success" pill>
                            <i className="ri-whatsapp-line align-middle fs-14 me-1"></i> WhatsApp
                        </Badge>
                    ) : (
                        <Badge color="primary" pill>
                            <i className="ri-mail-line align-middle fs-14 me-1"></i> Correo
                        </Badge>
                    );
                },
            },
            {
                header: "Plantilla",
                accessorKey: "templateName",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    return cellProps.getValue() || '-';
                },
            },
            {
                header: "Agente",
                accessorKey: "agentName",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    return cellProps.getValue() || '-';
                },
            },
            {
                header: "Criterios",
                accessorKey: "criteria",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    const criteria = cellProps.getValue();
                    const stringified = JSON.stringify(criteria);
                    return (
                        <div
                            className="text-truncate"
                            style={{ fontSize: '11px', maxWidth: '200px' }}
                            title={stringified}
                        >
                            {stringified}
                        </div>
                    );
                },
            },
            {
                header: "Afectados",
                accessorKey: "affectedRecords",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    return <h6 className="mb-0 fs-14">{cellProps.getValue()}</h6>;
                },
            },
            {
                header: "Estado",
                accessorKey: "status",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    const status = cellProps.getValue();
                    const row = cellProps.row.original;
                    switch (status) {
                        case 'COMPLETED':
                            return <Badge color="success" className="bg-success-subtle text-success">Completado</Badge>;
                        case 'PROCESSING':
                            return <Badge color="primary" className="bg-primary-subtle text-primary animate-pulse">Procesando</Badge>;
                        case 'FAILED':
                            return (
                                <div className="d-flex align-items-center gap-2">
                                    <Badge className="bg-primary-subtle text-primary">
                                        Fallo
                                    </Badge>

                                    <button
                                        type="button"
                                        className="btn p-0 border-0 bg-transparent text-primary"
                                        onClick={() => {
                                            setErrorMessage(row.errorMessage || 'No hay mensaje de error disponible');
                                            setErrorModalOpen(true);
                                        }}
                                        title="Ver error"
                                    >
                                        <i className="ri-information-line text-danger fs-4"></i>
                                    </button>
                                </div>
                            );
                        case 'PENDING':
                            return <Badge color="warning" className="bg-warning-subtle text-warning">Pendiente</Badge>;
                        default:
                            return <Badge color="secondary">{status}</Badge>;
                    }
                },
            },
            {
                header: "Fecha",
                accessorKey: "createdAt",
                enableColumnFilter: false,
                cell: (cellProps: any) => {
                    const dateString = cellProps.getValue();
                    if (!dateString) return '-';
                    const date = new Date(dateString);
                    return date.toLocaleString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                },
            },
        ],
        []
    );

    const handlePageChange = (pageIndex: number) => {
        const targetPage = pageIndex + 1;
        if (targetPage !== filters.page) {
            onPageChange(targetPage);
        }
    };

    return (
        <Row className="mb-4 justify-content-center">
            <TableContainer
                columns={columns}
                data={data || []}
                total={total}
                onPageChange={handlePageChange}
                isGlobalFilter={false}
                customPageSize={filters.limit || 10}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                SearchPlaceholder="Buscar registros..."
                isLoading={isLoading}
                isFetching={isFetching}
            />

            <AlertModal
                isOpen={errorModalOpen}
                toggle={() => setErrorModalOpen(false)}
                title="Detalle del Error"
                message={renderErrorMessage(errorMessage)}
                type="danger"
                confirmText="Cerrar"
            />
        </Row>
    );
};
